import { USD } from "@loot-os/api-contract";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@loot-os/ui/components/card";
import { toast } from "@loot-os/ui/components/sonner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ErrorAlert } from "../components/ErrorAlert";
import { ShippingDestinationsEditor } from "../components/ShippingDestinationsEditor";
import { useTRPC } from "../lib/trpc";

/**
 * The platform shipping default: the destinations every environment ships to until its tenant
 * customizes its own on the tenant's Shipping page. Platform admins only; costs read as dollars
 * here because there is no tenant to borrow a currency from, and each storefront shows them in
 * its own units.
 */
export function PlatformShippingPage() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const options = trpc.platform.shipping.get.queryOptions();
  const query = useQuery(options);
  const save = useMutation(
    trpc.platform.shipping.set.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: options.queryKey });
        toast.success("Platform shipping default saved.");
      },
      onError: (err) => {
        if (err.data?.code === "CONFLICT") {
          void queryClient.invalidateQueries({ queryKey: options.queryKey });
        }
      },
    }),
  );

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Shipping</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          The default destinations, costs and delivery estimates every environment inherits until
          its tenant customizes them. Costs are entered in dollars here; each storefront shows them
          in its own currency.
        </p>
      </div>
      {query.isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}
      {query.error && <ErrorAlert message={query.error.message} />}
      {query.data && (
        <Card>
          <CardHeader>
            <CardTitle>Platform default</CardTitle>
            <CardDescription>
              Saved whole: a save that lands on someone else's is refused, and the list reloads.
            </CardDescription>
          </CardHeader>
          <CardContent className="gap-4">
            <ShippingDestinationsEditor
              key={query.data.updatedAt ?? "unsaved"}
              destinations={query.data.destinations}
              currency={USD}
              canEdit
              saving={save.isPending}
              onSave={(destinations) =>
                save.mutate({ destinations, expectedUpdatedAt: query.data.updatedAt })
              }
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
